<template>
  <div class="row mt-4">
    <div class="col-12">
      <div class="card ficha-listado mb-4">
        <div class="px-4 pb-4 card-body">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h6 class="mb-0">{{ $t('panelCliente.suscrTitulo') }}</h6>
              <p class="text-xs opacity-7 mb-0">Total: <b>{{ suscripciones.length }}</b></p>
            </div>
          </div>

          <div v-if="suscripciones.length === 0" class="alert alert-secondary text-sm mb-0">
            {{ $t('panelCliente.suscrVacio') }}
          </div>

          <div v-else class="table-responsive">
            <table class="table table-flush table-listado table-empirulada">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('panelCliente.producto') }}</th>
                  <th>{{ $t('panelCliente.canal') }}</th>
                  <th>{{ $t('panelCliente.estado') }}</th>
                  <th>{{ $t('panelCliente.proximoCobro') }}</th>
                  <th>{{ $t('panelCliente.desde') }}</th>
                  <th class="text-center">{{ $t('panelCliente.accion') }}</th>
                </tr>
              </thead>
              <tbody class="text-sm">
                <tr v-for="s in paginadas" :key="s.id">
                  <td><div class="suscripcion-nombre">{{ s.producto?.nombre || "-" }}</div></td>
                  <td>{{ s.canal?.nombre || "-" }}</td>
                  <td>
                    <div class="d-flex flex-column align-items-start gap-1">
                      <span class="badge badge-estado" :class="claseBadge(s.estado)">
                        {{ labelEstado(s.estado) }}
                      </span>
                      <span v-if="diasRestantes(s) !== null" class="badge-dias-free">
                        {{ diasRestantes(s) === 0 ? $t('panelCliente.ultimoDiaFree') : $t('panelCliente.diasFree', { n: diasRestantes(s) }) }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span v-if="s.estado === 'activa' || s.estado === 'gracia'">
                      {{ fmtFecha(s.periodo_fin) }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>{{ fmtFecha(s.created_at) }}</td>
                  <td class="text-center">
                    <div class="d-flex gap-2 justify-content-center flex-wrap">
                      <!-- Cancelar (activa / gracia / vencida) -->
                      <button
                        v-if="s.estado === 'activa' || s.estado === 'gracia' || s.estado === 'vencida'"
                        class="btn btn-cancelar btn-sm"
                        :disabled="cancelando === s.id"
                        @click="confirmarCancelacion(s)"
                      >
                        <i v-if="cancelando === s.id" class="pi pi-spin pi-spinner" style="font-size:11px;margin-right:4px;" />
                        {{ cancelando === s.id ? $t('panelCliente.cancelando') : $t('panelCliente.cancelar') }}
                      </button>
                      <!-- Cambiar tarjeta (activa / gracia) -->
                      <button
                        v-if="s.estado === 'activa' || s.estado === 'gracia'"
                        class="btn btn-cambiar-tarjeta btn-sm"
                        :disabled="modalPago.loading"
                        @click="abrirCambiarTarjeta(s)"
                      >
                        💳 Cambiar tarjeta
                      </button>
                      <!-- Reactivar (vencida / cancelada) -->
                      <button
                        v-if="s.estado === 'vencida' || s.estado === 'cancelada'"
                        class="btn btn-reactivar btn-sm"
                        :disabled="modalPago.loading"
                        @click="abrirReactivar(s)"
                      >
                        🔄 Reactivar
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="totalPages > 1" class="paginacion-panel-cliente">
              <div class="paginacion-info text-xs opacity-7">
                {{ $t('panelCliente.suscrPagInfo', { desde, hasta, total: suscripciones.length }) }}
              </div>
              <div class="paginacion-botones">
                <button type="button" class="btn btn-paginacion" :disabled="page === 1" @click="page = 1">&lt;&lt;</button>
                <button type="button" class="btn btn-paginacion" :disabled="page === 1" @click="page--">&lt;</button>
                <span class="pagina-actual">{{ $t('panelCliente.paginaDeTotal', { page, total: totalPages }) }}</span>
                <button type="button" class="btn btn-paginacion" :disabled="page === totalPages" @click="page++">&gt;</button>
                <button type="button" class="btn btn-paginacion" :disabled="page === totalPages" @click="page = totalPages">&gt;&gt;</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Modal pago (reactivar / cambiar tarjeta) ─────────────────── -->
    <teleport to="body">
      <div v-if="modalPago.visible" class="modal-overlay" @click.self="cerrarModalPago">
        <div class="modal-pago-card">
          <button class="modal-pago-close" @click="cerrarModalPago">✕</button>

          <!-- Estado: cargando clientSecret -->
          <div v-if="modalPago.loading" class="modal-pago-loading">
            <div class="spinner-border text-warning" style="width:2rem;height:2rem;"></div>
            <p class="mt-3 mb-0 text-sm opacity-7">Conectando con el procesador de pagos…</p>
          </div>

          <!-- Estado: listo para pagar -->
          <template v-else-if="modalPago.clientSecret">
            <h6 class="modal-pago-titulo">
              {{ modalPago.modo === 'reactivar' ? '🔄 Reactivar suscripción' : '💳 Cambiar método de pago' }}
            </h6>
            <p class="modal-pago-subtitulo">
              <template v-if="modalPago.modo === 'reactivar'">
                Introduce tu tarjeta para reactivar <b>{{ modalPago.nombreProducto }}</b>.
              </template>
              <template v-else>
                Introduce tu nueva tarjeta para <b>{{ modalPago.nombreProducto }}</b>. Se usará en el próximo cobro.
              </template>
            </p>

            <PaymentStripeElement
              :clientSecret="modalPago.clientSecret"
              :publicKey="modalPago.publicKey"
              :email="userEmail"
              :mode="modalPago.modo"
              @cerrar-componente="onPagoCompletado"
            />
          </template>

          <!-- Estado: error al iniciar -->
          <div v-else class="modal-pago-loading">
            <p class="text-danger mb-0">No se pudo iniciar el proceso de pago. Inténtalo de nuevo.</p>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import showSwal from "@/mixins/showSwal.js";
import panelesService from "@/services/paneles.service.js";
import PaymentStripeElement from "@/components/PaymentStripeElement.vue";

export default {
  name: "PanelClienteSuscripciones",
  components: { PaymentStripeElement },
  props: {
    suscripciones: { type: Array, default: () => [] },
    userEmail:     { type: String, default: "" },
  },
  emits: ["recargar"],
  data() {
    return {
      page: 1,
      perPage: 5,
      cancelando: null,
      modalPago: {
        visible:        false,
        loading:        false,
        modo:           null,        // 'reactivar' | 'actualizarpago'
        suscripcionId:  null,
        clientSecret:   null,
        publicKey:      null,
        nombreProducto: "",
        paymentIntentId: null,       // solo para reactivar
      },
    };
  },
  watch: {
    suscripciones() { this.page = 1; },
  },
  computed: {
    totalPages() { return Math.max(1, Math.ceil(this.suscripciones.length / this.perPage)); },
    paginadas() {
      const start = (this.page - 1) * this.perPage;
      return this.suscripciones.slice(start, start + this.perPage);
    },
    desde() { return this.suscripciones.length === 0 ? 0 : (this.page - 1) * this.perPage + 1; },
    hasta() { return Math.min(this.page * this.perPage, this.suscripciones.length); },
  },
  methods: {
    claseBadge(estado) {
      return {
        activa:    "badge-ok",
        gracia:    "badge-warn",
        cancelada: "badge-muted",
        vencida:   "badge-err",
      }[estado] ?? "badge-muted";
    },
    labelEstado(estado) {
      const map = {
        activa:    this.$t('panelCliente.estadoActiva'),
        gracia:    this.$t('panelCliente.estadoGracia'),
        cancelada: this.$t('panelCliente.estadoCancelada'),
        vencida:   this.$t('panelCliente.estadoVencida'),
      };
      return map[estado] ?? estado;
    },
    diasRestantes(s) {
      if (!s.gracia_hasta) return null;
      const fin = new Date(s.gracia_hasta);
      if (Number.isNaN(fin.getTime())) return null;
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      fin.setHours(0, 0, 0, 0);
      const diff = Math.ceil((fin - hoy) / 86400000);
      return diff >= 0 ? diff : null;
    },
    fmtFecha(fechaStr) {
      if (!fechaStr) return "-";
      try {
        const d = new Date(fechaStr);
        if (Number.isNaN(d.getTime())) return fechaStr;
        const locale = this.$i18n?.locale === 'en' ? 'en-GB' : 'es-ES';
        return d.toLocaleDateString(locale);
      } catch { return fechaStr; }
    },
    async confirmarCancelacion(suscripcion) {
      const nombre = suscripcion.producto?.nombre || '';
      const confirmado = await showSwal.methods.showConfirm({
        type: "warning",
        title: this.$t('panelCliente.confirmarCancelTitulo'),
        message: this.$t('panelCliente.confirmarCancelMsg', { nombre }),
        width: 540,
        confirmButtonText: this.$t('panelCliente.siCancelar'),
        cancelButtonText: this.$t('panelCliente.no'),
      });
      if (!confirmado?.isConfirmed) return;
      await this.cancelarSuscripcion(suscripcion);
    },
    async cancelarSuscripcion(suscripcion) {
      this.cancelando = suscripcion.id;
      try {
        await panelesService.cancelarSuscripcion(suscripcion.id);
        showSwal.methods.showSwal({ type: "success", message: this.$t('panelCliente.canceladaOk'), width: 480 });
        this.$emit("recargar");
      } catch {
        showSwal.methods.showSwal({ type: "error", message: this.$t('panelCliente.canceladaErr'), width: 480 });
      } finally {
        this.cancelando = null;
      }
    },

    // ── Reactivar ──────────────────────────────────────────────────────
    async abrirReactivar(s) {
      this.modalPago = {
        visible:        true,
        loading:        true,
        modo:           "reactivar",
        suscripcionId:  s.id,
        clientSecret:   null,
        publicKey:      null,
        nombreProducto: s.producto?.nombre || "",
        paymentIntentId: null,
      };
      try {
        const data = await panelesService.iniciarReactivacion(s.id);
        if (data.res !== "OK") throw new Error(data.mensaje || "Error");
        this.modalPago.clientSecret    = data.clientSecret;
        this.modalPago.publicKey       = data.publicKey;
        this.modalPago.paymentIntentId = data.paymentIntentId;
        this.modalPago.loading         = false;
      } catch (err) {
        this.modalPago.loading       = false;
        this.modalPago.clientSecret  = null;
        showSwal.methods.showSwal({ type: "error", message: err.message || "No se pudo iniciar la reactivación", width: 500 });
      }
    },

    // ── Cambiar tarjeta ────────────────────────────────────────────────
    async abrirCambiarTarjeta(s) {
      this.modalPago = {
        visible:        true,
        loading:        true,
        modo:           "actualizarpago",
        suscripcionId:  s.id,
        clientSecret:   null,
        publicKey:      null,
        nombreProducto: s.producto?.nombre || "",
        paymentIntentId: null,
      };
      try {
        const data = await panelesService.iniciarActualizarPago(s.id);
        if (data.res !== "OK") throw new Error(data.mensaje || "Error");
        this.modalPago.clientSecret = data.clientSecret;
        this.modalPago.publicKey    = data.publicKey;
        this.modalPago.loading      = false;
      } catch (err) {
        this.modalPago.loading      = false;
        this.modalPago.clientSecret = null;
        showSwal.methods.showSwal({ type: "error", message: err.message || "No se pudo iniciar el proceso", width: 500 });
      }
    },

    cerrarModalPago() {
      this.modalPago.visible = false;
    },

    // ── Callback de PaymentStripeElement ──────────────────────────────
    async onPagoCompletado(res, intentId) {
      if (res !== "OK") {
        showSwal.methods.showSwal({ type: "error", message: intentId || "Error en el pago", width: 500 });
        return;
      }

      const { modo, suscripcionId } = this.modalPago;
      this.cerrarModalPago();

      try {
        let respuesta;
        if (modo === "reactivar") {
          respuesta = await panelesService.confirmarReactivacion(suscripcionId, intentId);
        } else {
          respuesta = await panelesService.confirmarActualizarPago(suscripcionId, intentId);
        }

        if (respuesta.res === "OK") {
          if (modo === "reactivar") {
            showSwal.methods.showSwal({
              type: "success",
              message: respuesta.invite_link
                ? `Suscripción reactivada. Revisa tu email para el enlace de acceso al canal.`
                : "Suscripción reactivada correctamente.",
              width: 520,
            });
          } else {
            showSwal.methods.showSwal({ type: "success", message: "Método de pago actualizado correctamente.", width: 480 });
          }
          this.$emit("recargar");
        } else {
          showSwal.methods.showSwal({ type: "error", message: respuesta.mensaje || "Error al confirmar", width: 480 });
        }
      } catch {
        showSwal.methods.showSwal({ type: "error", message: "Error al comunicarse con el servidor.", width: 480 });
      }
    },
  },
};
</script>

<style scoped>
.suscripcion-nombre { font-weight: 700; color: var(--q-text); line-height: 1.15; }

.badge-estado {
  display: inline-flex; align-items: center; justify-content: center;
  height: 26px; min-width: 80px; padding: 0 12px; border-radius: 999px;
  font-size: 11px; font-weight: 700;
}
.badge-ok   { background: var(--q-green-soft); color: var(--q-green);  border: 1px solid var(--q-green); }
.badge-warn { background: var(--q-amber-soft); color: var(--q-amber);  border: 1px solid var(--q-amber); }
.badge-err  { background: var(--q-red-soft);   color: var(--q-red);    border: 1px solid var(--q-red); }
.badge-muted{ background: var(--q-hover);      color: var(--q-muted);  border: 1px solid var(--q-border-md); }

.badge-dias-free {
  display: inline-flex; align-items: center;
  padding: 2px 9px; border-radius: 999px;
  font-size: 10px; font-weight: 700; letter-spacing: .02em;
  background: var(--q-gold-soft, rgba(212,175,55,.15));
  color: var(--q-gold, #d4af37);
  border: 1px solid var(--q-gold, #d4af37);
  white-space: nowrap;
}

.btn-cancelar {
  padding: 4px 12px; border-radius: 8px; font-size: 12px; font-weight: 700;
  background: rgba(220,53,69,.12); color: var(--q-red, #dc3545);
  border: 1px solid var(--q-red, #dc3545);
  transition: .15s;
}
.btn-cancelar:hover:not(:disabled) { background: rgba(220,53,69,.22); }
.btn-cancelar:disabled { opacity: .5; cursor: not-allowed; }

.btn-reactivar {
  padding: 4px 12px; border-radius: 8px; font-size: 12px; font-weight: 700;
  background: rgba(0,200,150,.12); color: var(--q-green, #00c896);
  border: 1px solid var(--q-green, #00c896);
  transition: .15s;
}
.btn-reactivar:hover:not(:disabled) { background: rgba(0,200,150,.22); }
.btn-reactivar:disabled { opacity: .5; cursor: not-allowed; }

.btn-cambiar-tarjeta {
  padding: 4px 12px; border-radius: 8px; font-size: 12px; font-weight: 700;
  background: rgba(147,112,219,.12); color: #9370db;
  border: 1px solid #9370db;
  transition: .15s;
}
.btn-cambiar-tarjeta:hover:not(:disabled) { background: rgba(147,112,219,.22); }
.btn-cambiar-tarjeta:disabled { opacity: .5; cursor: not-allowed; }

/* ── Modal overlay ── */
.modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.65); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.modal-pago-card {
  position: relative;
  background: var(--q-card, #1a2535);
  border: 1px solid var(--q-border-md, rgba(255,255,255,.1));
  border-radius: 16px;
  padding: 2rem;
  width: 100%; max-width: 520px;
  max-height: 90vh; overflow-y: auto;
}
.modal-pago-close {
  position: absolute; top: 12px; right: 14px;
  background: none; border: none; font-size: 18px;
  color: var(--q-muted); cursor: pointer; line-height: 1;
}
.modal-pago-close:hover { color: var(--q-text); }
.modal-pago-titulo {
  color: var(--q-gold, #ffd700); font-weight: 700; font-size: 1.1rem; margin-bottom: .4rem;
}
.modal-pago-subtitulo {
  font-size: 0.88rem; color: var(--q-muted); margin-bottom: 0;
}
.modal-pago-loading {
  display: flex; flex-direction: column; align-items: center; padding: 2rem 0;
}

/* ── Tabla empirulada ── */
.table-empirulada { border-collapse: separate !important; border-spacing: 0 10px; }
.table-empirulada thead th { border: 0 !important; background: var(--q-hover); color: var(--q-text); font-weight: 700; padding: 12px 14px; }
.table-empirulada tbody tr { background: var(--q-card-alt); box-shadow: 0 6px 18px rgba(0,0,0,.06); }
.table-empirulada tbody td { border: 0 !important; padding: 12px 14px; vertical-align: middle; }
.table-empirulada > :not(caption) > * > * { border-bottom-width: 0 !important; }
.table-empirulada tbody tr:hover { transform: translateY(-1px); transition: .15s ease; }
.table-empirulada tbody tr td:first-child { border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
.table-empirulada tbody tr td:last-child { border-top-right-radius: 12px; border-bottom-right-radius: 12px; }
.table-empirulada thead, .table-empirulada thead tr, .table-empirulada thead th { border-radius: 0 !important; }
.table-empirulada thead tr { box-shadow: none !important; background: transparent !important; }

.paginacion-panel-cliente { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 16px; padding-top: 10px; }
.paginacion-botones { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.btn-paginacion { min-width: 38px; height: 38px; padding: 0 10px; border: 0; border-radius: 10px; background: var(--q-gold); color: var(--q-gold-on); font-weight: 700; box-shadow: 0 4px 10px rgba(0,0,0,.12); }
.btn-paginacion:disabled { opacity: .45; cursor: not-allowed; box-shadow: none; }
.pagina-actual { font-size: 13px; font-weight: 600; color: var(--q-text); }

@media (max-width: 768px) {
  .paginacion-panel-cliente { flex-direction: column; align-items: flex-start; }
  .modal-pago-card { padding: 1.25rem 1rem; border-radius: 12px; }
  .modal-pago-titulo { font-size: 1rem; }
}
</style>
